import React, { useEffect, useRef } from 'react';
import { X, Clock } from 'lucide-react';
import ModelSelector from './ModelSelector';

const whiteKingUrl = '/chess-pieces/wK.png';
const blackKingUrl = '/chess-pieces/bK.png';

type GameSetupProps = {
  selectedModel: string;
  playerColor: 'white' | 'black' | 'random';
  useVision: boolean;
  gameTimeLimit: 3 | 5 | 10;
  onModelChange: (model: string, useVision: boolean) => void;
  onPlayerColorChange: (color: 'white' | 'black' | 'random') => void;
  onGameTimeLimitChange: (minutes: 3 | 5 | 10) => void;
  onResetGame: (startNewGame?: boolean) => void;
  onClose?: () => void;
  isMobile?: boolean;
};

const GameSetup: React.FC<GameSetupProps> = ({
  selectedModel,
  playerColor,
  useVision,
  gameTimeLimit,
  onModelChange,
  onPlayerColorChange,
  onGameTimeLimitChange,
  onResetGame,
  onClose,
  isMobile = false
}) => {
  const containerRef = useRef<HTMLDivElement>(null);

  // Handle click outside to close on mobile
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isMobile && onClose && containerRef.current && !containerRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMobile, onClose]);

  return (
    <div ref={containerRef} className="flex flex-col gap-5">
      {/* Close button for mobile */}
      {isMobile && onClose && (
        <div className="self-end">
          <button 
            onClick={onClose}
            className="text-terminal-green hover:text-white transition-colors bg-dark-green rounded-full p-1"
            aria-label="Close"
          >
            <X size={24} />
          </button>
        </div>
      )}
      
      {/* New custom model selector component */}
      <ModelSelector 
        selectedModel={selectedModel}
        useVision={useVision}
        onModelChange={onModelChange}
      />
      
      <div className="flex flex-col items-start gap-3">
        <label className="text-left font-vt323 text-xl">$ I PLAY AS:</label>
        <div className="grid grid-cols-3 gap-2 w-full">
          <button
            className={`p-2 border rounded ${playerColor === 'white' ? 'border-terminal-green bg-dark-green' : 'border-terminal-green/50 bg-bg-dark'} flex items-center justify-center transition-all hover:bg-dark-green/70`}
            onClick={() => onPlayerColorChange('white')}
          >
            <div className="w-10 h-10 bg-white/10 rounded-md flex items-center justify-center">
              <img src={whiteKingUrl} alt="White King" className="w-10 h-10 object-contain rounded-md" />
            </div>
          </button>
          <button
            className={`p-2 border rounded ${playerColor === 'random' ? 'border-terminal-green bg-dark-green' : 'border-terminal-green/50 bg-bg-dark'} flex items-center justify-center transition-all hover:bg-dark-green/70`}
            onClick={() => onPlayerColorChange('random')}
          >
            <div className="w-10 h-10 bg-white/10 rounded-md flex items-center justify-center text-terminal-green text-2xl font-bold">
              ?
            </div>
          </button>
          <button
            className={`p-2 border rounded ${playerColor === 'black' ? 'border-terminal-green bg-dark-green' : 'border-terminal-green/50 bg-bg-dark'} flex items-center justify-center transition-all hover:bg-dark-green/70`}
            onClick={() => onPlayerColorChange('black')}
          >
            <div className="w-10 h-10 bg-white/10 rounded-md flex items-center justify-center">
              <img src={blackKingUrl} alt="Black King" className="w-10 h-10 object-contain rounded-md" />
            </div>
          </button>
        </div>
      </div>
      {/* Time control selection */}
      <div className="flex flex-col items-start gap-3">
        <label className="text-left font-vt323 text-xl">$ TIME CONTROL:</label>
        <div className="grid grid-cols-3 gap-2 w-full">
          <button
            className={`p-2 border rounded ${gameTimeLimit === 3 ? 'border-terminal-green bg-dark-green' : 'border-terminal-green/50 bg-bg-dark'} flex items-center justify-center transition-all hover:bg-dark-green/70`}
            onClick={() => onGameTimeLimitChange(3)}
          >
            <div className="w-10 h-10 bg-white/10 rounded-md flex items-center justify-center text-terminal-green">
              <Clock size={18} className="mr-1" />
              <span>3</span>
            </div>
          </button>
          <button
            className={`p-2 border rounded ${gameTimeLimit === 5 ? 'border-terminal-green bg-dark-green' : 'border-terminal-green/50 bg-bg-dark'} flex items-center justify-center transition-all hover:bg-dark-green/70`}
            onClick={() => onGameTimeLimitChange(5)}
          >
            <div className="w-10 h-10 bg-white/10 rounded-md flex items-center justify-center text-terminal-green">
              <Clock size={18} className="mr-1" />
              <span>5</span>
            </div>
          </button>
          <button
            className={`p-2 border rounded ${gameTimeLimit === 10 ? 'border-terminal-green bg-dark-green' : 'border-terminal-green/50 bg-bg-dark'} flex items-center justify-center transition-all hover:bg-dark-green/70`}
            onClick={() => onGameTimeLimitChange(10)}
          >
            <div className="w-10 h-10 bg-white/10 rounded-md flex items-center justify-center text-terminal-green">
              <Clock size={18} className="mr-1" />
              <span>10</span>
            </div>
          </button>
        </div>
      </div>
      <button
        className="w-full bg-bg-dark text-terminal-green border rounded border-terminal-green p-3 font-vt323 text-xl cursor-pointer hover:bg-dark-green"
        onClick={() => {
          onResetGame(true); 
          if (isMobile && onClose) {
            onClose();
          }
        }}
      >
        <span className="font-vt323 text-2xl animate-pulse">$ PRESS START</span>
      </button>
    </div>
  );
};

export default GameSetup;